/**
 * OAuth Error Page
 * Displays user-friendly error messages when OAuth flow fails
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Home } from 'lucide-react';
import Footer from '../components/Footer';

interface ErrorDetails {
  code: string;
  message: string;
  provider?: string;
}

// User-facing error codes (safe to show)
const USER_FACING_ERRORS = [
  'invalid_state',
  'access_denied',
  'missing_code',
  'unsupported_provider',
  'account_already_connected',
  // GitHub App installation errors
  'missing_installation_id',
  'installation_failed',
  'connection_failed',
  'user_creation_failed',
  'missing_state',
  'provider_mismatch',
];

const ERROR_MESSAGES: Record<string, { title: string; description: string; suggestion: string }> = {
  invalid_state: {
    title: 'Session Expired',
    description:
      'Your sign-in session has expired. This can happen if you took too long to complete the process.',
    suggestion: 'Please start the sign-in process again.',
  },
  access_denied: {
    title: 'Access Denied',
    description: 'You cancelled the sign-in process or denied access to your account.',
    suggestion:
      'If you want to connect this account, please try again and grant the necessary permissions.',
  },
  missing_code: {
    title: 'Authorization Failed',
    description: 'The authorization code was not received from the provider.',
    suggestion: 'Please try signing in again.',
  },
  unsupported_provider: {
    title: 'Unsupported Provider',
    description: 'The selected sign-in method is not supported.',
    suggestion: 'Please use a different sign-in method.',
  },
  account_already_connected: {
    title: 'Account Already Connected',
    description: 'This account is already connected to a different user.',
    suggestion:
      'Sign in with the account that has this provider connected, or disconnect it from that account first.',
  },
  // GitHub App installation errors
  missing_installation_id: {
    title: 'Installation Incomplete',
    description: 'The GitHub App installation did not complete properly.',
    suggestion:
      'Please go to GitHub Settings > Applications and try installing the Autar app again.',
  },
  installation_failed: {
    title: 'Installation Failed',
    description: 'We encountered an issue while setting up the GitHub App installation.',
    suggestion:
      'Please try again. If the problem persists, check your GitHub App settings or contact support.',
  },
  connection_failed: {
    title: 'Connection Failed',
    description: 'We could not connect your GitHub account.',
    suggestion:
      'Please sign in first, then try connecting your GitHub account from the Repositories page.',
  },
  user_creation_failed: {
    title: 'Account Creation Failed',
    description: 'We could not create your account.',
    suggestion: 'Please try again. If you already have an account, try signing in instead.',
  },
  missing_state: {
    title: 'Security Check Failed',
    description: 'The security token was missing from the request.',
    suggestion: 'Please start the sign-in process again from the beginning.',
  },
  provider_mismatch: {
    title: 'Provider Mismatch',
    description: 'The authentication provider did not match the expected one.',
    suggestion: 'Please try signing in again and ensure you select the correct provider.',
  },
  default: {
    title: 'Sign-In Error',
    description: 'We encountered an issue while signing you in.',
    suggestion: 'Please try again. If the problem persists, contact support.',
  },
};

export const OAuthErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);

  useEffect(() => {
    const rawErrorCode = searchParams.get('error') || 'default';
    const errorMessage = searchParams.get('message') || '';
    const provider = searchParams.get('provider') || '';

    // Only use specific error codes if they're in the safe list
    // For all internal errors (DB, server issues, etc), use 'default'
    const errorCode = USER_FACING_ERRORS.includes(rawErrorCode) ? rawErrorCode : 'default';

    setErrorDetails({
      code: errorCode,
      message: errorMessage,
      provider,
    });
  }, [searchParams]);

  if (!errorDetails) {
    return null;
  }

  const errorInfo = ERROR_MESSAGES[errorDetails.code] || ERROR_MESSAGES['default'];
  const providerName = errorDetails.provider
    ? errorDetails.provider.charAt(0).toUpperCase() + errorDetails.provider.slice(1)
    : 'OAuth Provider';

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
              {errorInfo.title}
            </h1>

            {/* Provider Badge */}
            {errorDetails.provider && (
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {providerName}
                </span>
              </div>
            )}

            {/* Error Description */}
            <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
              {errorDetails.message || errorInfo.description}
            </p>

            {/* Suggestion */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong className="font-semibold">Suggestion:</strong> {errorInfo.suggestion}
              </p>
            </div>

            {/* Error Code (for debugging - only show safe error codes) */}
            {USER_FACING_ERRORS.includes(errorDetails.code) && (
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-6">
                Error Code: {errorDetails.code}
              </p>
            )}

            {/* Action Button */}
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Home className="w-4 h-4" />
              Return to Home
            </button>

            {/* Help Text */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
              If you continue to experience issues, please contact support.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OAuthErrorPage;
